<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { api } from '../api'
import { useI18n } from '../i18n'
import { chooseNaftaPosSource, hasPermission, session } from '../session'
import NaftaPosInstanceSelector from '../components/NaftaPosInstanceSelector.vue'
import { activeNaftaPosInstance } from '../platform/context'

const { t } = useI18n()

const activeStation = computed(() => activeNaftaPosInstance(session.platformContext))
const stationId = computed(() => activeStation.value?.enabled ? String(activeStation.value.id) : '')
const switching = ref(false)
const items = ref([])
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const notice = ref('')
const form = ref({ tankNumber: 1, capacityLiters: null })
const canConfigure = computed(() => hasPermission('tank_capacities.configure'))
const canDelete = computed(() => hasPermission('tank_capacities.delete'))
let requestNumber = 0
let controller = null
let disposed = false
const sourceScope = () => JSON.stringify([session.user?.id ?? session.user?.username,
  session.user?.accessVersion, session.platformContext?.activeStationId,
  activeStation.value?.id, activeStation.value?.enabled])
const currentScope = (scope) => !disposed && scope === sourceScope()

async function selectStation(sourceId) {
  if (!sourceId || String(sourceId) === stationId.value || switching.value || saving.value) return
  const scope = sourceScope()
  switching.value = true
  clearSourceData()
  try { await chooseNaftaPosSource(sourceId) }
  catch (cause) { if (currentScope(scope)) error.value = cause.message }
  finally { switching.value = false }
}

function clearSourceData() {
  requestNumber++
  controller?.abort()
  items.value = []; error.value = ''; notice.value = ''
  form.value = { tankNumber: 1, capacityLiters: null }
}
async function loadItems() {
  clearSourceData()
  const request = ++requestNumber, scope = sourceScope(), sourceId = stationId.value
  const isCurrent = () => request === requestNumber && currentScope(scope)
  if (!sourceId) { loading.value = false; return }
  controller = new AbortController()
  loading.value = true
  error.value = ''; notice.value = ''
  try {
    const result = await api(`/admin/stations/${sourceId}/tank-capacities`, { signal: controller.signal })
    if (isCurrent()) items.value = result.items
  } catch (cause) { if (cause.name !== 'AbortError' && isCurrent()) error.value = cause.message }
  finally { if (isCurrent()) loading.value = false }
}

async function save() {
  if (!stationId.value || saving.value || loading.value || switching.value || !canConfigure.value) return
  const scope = sourceScope(), sourceId = stationId.value
  const tankNumber = Number(form.value.tankNumber)
  const capacityLiters = Number(form.value.capacityLiters)
  if (!Number.isInteger(tankNumber) || tankNumber < 1 || !Number.isFinite(capacityLiters) || capacityLiters <= 0) {
    error.value = t('capacityInvalid')
    return
  }
  saving.value = true; error.value = ''; notice.value = ''
  try {
    const result = await api(`/admin/stations/${sourceId}/tank-capacities/${tankNumber}`, { method: 'PUT', body: JSON.stringify({ capacityLiters }) })
    if (!currentScope(scope)) return
    items.value = [...items.value.filter((item) => item.tankNumber !== tankNumber), result.item].sort((a, b) => a.tankNumber - b.tankNumber)
    notice.value = t('capacitySaved', { number: tankNumber })
  } catch (cause) { if (currentScope(scope)) error.value = cause.message }
  finally { saving.value = false }
}

function edit(item) { form.value = { tankNumber: item.tankNumber, capacityLiters: item.capacityLiters }; window.scrollTo({ top: 0, behavior: 'smooth' }) }

async function remove(item) {
  if (!stationId.value || saving.value || loading.value || switching.value || !canDelete.value) return
  const scope = sourceScope(), sourceId = stationId.value
  if (!window.confirm(`Видалити налаштування місткості резервуара №${item.tankNumber}?`)) return
  saving.value = true
  error.value = ''; notice.value = ''
  try {
    await api(`/admin/stations/${sourceId}/tank-capacities/${item.tankNumber}`, { method: 'DELETE' })
    if (!currentScope(scope)) return
    items.value = items.value.filter((current) => current.tankNumber !== item.tankNumber)
    notice.value = t('capacityDeleted', { number: item.tankNumber })
  } catch (cause) { if (currentScope(scope)) error.value = cause.message }
  finally { saving.value = false }
}

onMounted(loadItems)
watch(sourceScope, loadItems, { flush: 'sync' })
onBeforeUnmount(() => { disposed = true; requestNumber++; controller?.abort() })
</script>

<template>
  <section class="tank-config-page">
    <div class="page-title"><div><p class="eyebrow">{{ t('configuration') }}</p><h1>{{ t('tankCapacities') }}</h1></div></div>
    <p class="muted">{{ t('tankCapacityHint') }}</p>
    <p v-if="!canConfigure && !canDelete" class="notice info" role="status">{{ t('viewOnlyHint') }}</p>
    <p v-if="error" class="notice error" role="alert">{{ error }}</p><p v-if="notice" class="notice success" role="status">{{ notice }}</p>
    <section class="card config-station-card"><NaftaPosInstanceSelector :context="session.platformContext" :disabled="loading || switching || saving" @select="selectStation" /></section>
    <div v-if="stationId" class="tank-config-grid" :class="{ 'two-columns': canConfigure }">
      <section v-if="canConfigure" class="card"><h2>Додати або змінити місткість</h2><form class="form-stack" @submit.prevent="save"><label>{{ t('tankNumber') }}<input v-model.number="form.tankNumber" type="number" min="1" step="1" required></label><label>{{ t('capacityLiters') }}<input v-model.number="form.capacityLiters" type="number" min="0.01" step="0.01" required></label><button class="button" type="submit" :disabled="saving">{{ saving ? 'Зберігаємо…' : t('saveCapacity') }}</button></form></section>
      <section class="card"><h2>Налаштовані резервуари</h2><div v-if="items.length" class="table-wrap"><table><thead><tr><th>Резервуар</th><th>{{ t('capacityLiters') }}</th><th>Оновив</th><th v-if="canConfigure || canDelete"></th></tr></thead><tbody><tr v-for="item in items" :key="item.tankNumber"><th>№{{ item.tankNumber }}</th><td>{{ new Intl.NumberFormat('uk-UA').format(item.capacityLiters) }}</td><td>{{ item.updatedBy || '—' }}</td><td v-if="canConfigure || canDelete" class="station-actions"><button v-if="canConfigure" class="button button-small button-secondary" type="button" @click="edit(item)">Змінити</button><button v-if="canDelete" class="button button-small button-danger" type="button" @click="remove(item)">Видалити</button></td></tr></tbody></table></div><p v-else class="muted">{{ t('capacityEmpty') }}</p></section>
    </div>
  </section>
</template>
